import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useApi } from '../hooks/useApi';
import { isInProgressStatus } from '../utils/reportHelpers';

const getNormalizedStatus = (status) => (status || '').toUpperCase();
const IN_PROGRESS_POLL_MS = 15000;

const deriveReportStatus = (status, completedAt, hasSummary = false, createdAt = null) => {
  const normalized = getNormalizedStatus(status);

  // Failed reports are always failed
  if (normalized === 'FAILED') {
    return normalized;
  }

  // Explicit completion statuses
  if (normalized === 'COMPLETED') {
    return 'COMPLETED';
  }

  // If report has completedAt timestamp OR has a summary, it's completed
  // regardless of what the status field says
  if ((completedAt || hasSummary) && (!normalized || normalized === 'RUNNING' || normalized === 'PROCESSING' || normalized === 'PENDING' || normalized === 'IN_PROGRESS' || normalized === 'SUBMITTED' || normalized === 'STARTED')) {
    return 'COMPLETED';
  }

  // Treat old batch_processing reports as completed (likely stuck/stale)
  // If created more than 1 hour ago and still showing batch_processing, consider it done
  if (normalized === 'BATCH_PROCESSING' && createdAt) {
    const ageMs = Date.now() - new Date(createdAt).getTime();
    const oneHourMs = 60 * 60 * 1000;
    if (ageMs > oneHourMs) {
      return 'COMPLETED';
    }
  }

  return normalized;
};

const mapRunToReport = (run) => {
  const completedAt = run.completed_at || run.completedAt || null;
  const createdAt = run.created_at || run.createdAt || completedAt || null;
  const hasSummary = !!(run.summary);
  const status = deriveReportStatus(run.status, completedAt, hasSummary, createdAt);
  // Get created_by from report - backend prepares this for display (PC-145)
  const createdByRaw = run.created_by
    || run.createdBy
    || run.user_email
    || run.userEmail
    || run.user_name
    || run.userName
    || run.owner
    || run.owner_email
    || run.ownerEmail
    || run.user?.email
    || run.user?.name
    || 'Unknown';  // FIX PC-139: Show 'Unknown' instead of current user

  const createdBy = createdByRaw?.includes('@')
    ? createdByRaw.split('@')[0]
    : createdByRaw;

  return {
    id: run.run_id || run.id,
    type: run.type || 'Full Sweep Report',
    createdAt,
    completedAt,
    status,
    pairCount: typeof run.pair_count === 'number'
      ? run.pair_count
      : (typeof run.pairCount === 'number' ? run.pairCount : 0),
    documentCount: run.document_count ?? run.documentCount ?? (
      Array.isArray(run.document_ids) ? run.document_ids.length : undefined
    ),
    documentIds: run.document_ids || run.documentIds,
    summary: run.summary,
    errorMessage: run.error_message || run.errorMessage,
    createdBy,
    comparisonType: run.comparison_type || null,
    primaryDocName: run.filename_a || null,
    referenceDocName: run.filename_b || null,
    htmlReportKey: run.html_report_key || null,
  };
};

const sortReportsByRecency = (reportList) => {
  const getTimestamp = (report) => new Date(report.createdAt || report.completedAt || 0).getTime();
  return [...reportList].sort((a, b) => getTimestamp(b) - getTimestamp(a));
};

const ReportContext = createContext(null);

export function useReports() {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error('useReports must be used within ReportProvider');
  }
  return context;
}

export function ReportProvider({ children }) {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { fetchReports, fetchJobStatus, deleteReport: apiDeleteReport, cancelRun } = useApi();
  const [reports, setReports] = useState([]);
  const [bookmarks, setBookmarks] = useState(JSON.parse(localStorage.getItem("bookmarks") || "[]"));
  const loadingRef = useRef(false);

  const loadReports = useCallback(async () => {
    // Prevent concurrent loads
    if (loadingRef.current) {
      return;
    }
    loadingRef.current = true;

    try {
      const data = await fetchReports();
      const mappedReports = (data || []).map((run) => mapRunToReport(run));
      setReports(sortReportsByRecency(mappedReports));
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      loadingRef.current = false;
    }
  }, [fetchReports]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadReports();
    }
  }, [authLoading, isAuthenticated, loadReports]);

  useEffect(() => {
    if (!reports.length) return;
    const inProgress = reports.filter((report) => {
      // Don't poll if the derived status is not in progress
      // This filters out old batch_processing reports that we've marked as complete
      if (!isInProgressStatus(report.status)) return false;
      if (typeof report.id !== 'string') return false;
      return !report.id.startsWith('temp-');
    });

    if (inProgress.length === 0) return;

    let cancelled = false;
    let initialDelay;
    let intervalTimer;

    const pollStatuses = async () => {
      try {
        const results = await Promise.allSettled(
          inProgress.map((report) => fetchJobStatus(report.id))
        );

        if (cancelled) return;

        setReports((prev) => {
          let updated = prev;

          results.forEach((result, index) => {
            if (result.status !== 'fulfilled') return;
            const response = result.value;
            const reportId = response.run_id || response.id || inProgress[index].id;
            const mapped = mapRunToReport(response);
            updated = updated.map((report) => {
              if (report.id !== reportId) return report;

              // Prevent status from going backwards - completed/failed reports stay that way
              const isCurrentlyComplete = report.status === 'COMPLETED' || report.status === 'FAILED';
              const shouldPreserveStatus = isCurrentlyComplete &&
                (mapped.status === 'RUNNING' || mapped.status === 'PROCESSING' || mapped.status === 'BATCH_PROCESSING');

              return {
                ...report,
                ...mapped,
                status: shouldPreserveStatus ? report.status : mapped.status,
                createdAt: mapped.createdAt || report.createdAt,
                completedAt: mapped.completedAt || report.completedAt,
                createdBy: report.createdBy  // Preserve original creator - don't overwrite with current user
              };
            });
          });

          return sortReportsByRecency(updated);
        });
      } catch (error) {
        console.error('Failed to poll report status:', error);
      }
    };

    // Delay first poll to avoid flash on page load
    initialDelay = setTimeout(() => {
      if (!cancelled) {
        pollStatuses();
        intervalTimer = setInterval(pollStatuses, IN_PROGRESS_POLL_MS);
      }
    }, 2000);

    return () => {
      cancelled = true;
      clearTimeout(initialDelay);
      clearInterval(intervalTimer);
    };
  }, [reports, user, fetchJobStatus]);

  const addReport = useCallback((report) => {
    const normalizedReport = mapRunToReport(report);
    setReports((prev) => sortReportsByRecency([
      normalizedReport,
      ...prev.filter((existing) => existing.id !== normalizedReport.id)
    ]));
  }, []);

  const toggleBookmark = useCallback((reportId) => {
    setBookmarks((prev) => {
      let newBookmarks = [];
      if (prev.includes(reportId)) {
        newBookmarks = prev.filter((id) => id !== reportId);
      } else {
        newBookmarks = [...prev, reportId]
      }
      
      localStorage.setItem("bookmarks", JSON.stringify(newBookmarks));
      
      return newBookmarks;
    });
  }, []);

  const isBookmarked = useCallback((reportId) => {
    return bookmarks.includes(reportId);
  }, [bookmarks]);

  const removeReport = useCallback((reportId) => {
    setReports((prev) => prev.filter(report => report.id !== reportId));
    setBookmarks((prev) => {
      const newBookmarks = prev.filter(id => id !== reportId)
      
      localStorage.setItem("bookmarks", JSON.stringify(newBookmarks));
      
      return newBookmarks;
    });
  }, []);

  const deleteReport = useCallback(async (reportId) => {
    try {
      await apiDeleteReport(reportId);
      removeReport(reportId);
    } catch (error) {
      console.error('Failed to delete report:', error);
      throw error;
    }
  }, [apiDeleteReport, removeReport]);

  const cancelReport = useCallback(async (reportId) => {
    await cancelRun(reportId);
    setReports((prev) =>
      prev.map((r) => r.id === reportId ? { ...r, status: 'CANCELLED' } : r)
    );
  }, [cancelRun]);

  const value = useMemo(() => ({
    reports,
    bookmarks,
    addReport,
    removeReport,
    toggleBookmark,
    isBookmarked,
    deleteReport,
    cancelReport,
    refreshReports: loadReports
  }), [reports, bookmarks, addReport, removeReport, toggleBookmark, isBookmarked, deleteReport, cancelReport, loadReports]);

  return <ReportContext.Provider value={value}>{children}</ReportContext.Provider>;
}
